import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaClock,
  FaCode,
  FaCommentDots,
  FaGraduationCap,
  FaSearch,
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useSeoMeta } from '../hooks/useSeoMeta.ts';
import { useClickOutside } from '../hooks/useClickOutside.ts';
import { problemPath } from '../utils/problemUrls.ts';
import './css/ClassEditPage.css';
import './css/StudentClassPage.css';

export type StudentProblemStatus = 'accepted' | 'attempted' | 'none';

export type StudentClassData = {
  role: 'student';
  id: number;
  name: string;
  teacher: {
    id: number;
    username: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  progress: { solved: number; attempted: number; total: number };
  lessons: {
    id: number;
    title: string;
    order: number;
    is_published?: boolean;
    due_at?: string | null;
    problems: {
      id: number;
      problem_id: number;
      order: number;
      status: StudentProblemStatus;
      has_feedback: boolean;
      feedback_unread?: boolean;
      problem: {
        id: number;
        title: string;
        difficulty: string;
        topic?: string | null;
        slug?: string | null;
        unlisted?: boolean;
      } | null;
    }[];
  }[];
  joined_classes: { id: number; name: string }[];
};

type StudentClassPageProps = {
  data: StudentClassData;
};

function teacherDisplayName(
  teacher: StudentClassData['teacher'],
  fallback: string,
): string {
  if (!teacher) return fallback;
  const full = [teacher.firstName, teacher.lastName].filter(Boolean).join(' ').trim();
  return full || teacher.username || fallback;
}

function difficultyColor(d: string): string {
  if (d === 'Easy') return 'var(--nn-difficulty-easy)';
  if (d === 'Medium') return 'var(--nn-difficulty-medium)';
  return 'var(--nn-difficulty-hard)';
}

function studyProblemTo(
  item: StudentClassData['lessons'][number]['problems'][number],
  classId: number,
  lessonId: number,
) {
  const base = item.problem ? problemPath(item.problem) : `/problems/${item.problem_id}`;
  return `${base}?class=${classId}&lesson=${lessonId}`;
}

function localTodayYmd() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function lessonIsIncomplete(lesson: StudentClassData['lessons'][number]) {
  return lesson.problems.length > 0 && lesson.problems.some((p) => p.status !== 'accepted');
}

function pickNextLesson(lessons: StudentClassData['lessons'], focusLessonId: number) {
  if (Number.isInteger(focusLessonId) && focusLessonId > 0) {
    const focused = lessons.find((lesson) => lesson.id === focusLessonId);
    if (focused) return focused;
  }
  const today = localTodayYmd();
  const incomplete = lessons.filter(lessonIsIncomplete);
  if (!incomplete.length) return lessons[0] || null;
  const withDue = incomplete.filter((lesson) => lesson.due_at);
  const overdue = withDue
    .filter((lesson) => String(lesson.due_at) < today)
    .sort((a, b) => String(a.due_at).localeCompare(String(b.due_at)));
  if (overdue.length) return overdue[0];
  const upcoming = withDue
    .filter((lesson) => String(lesson.due_at) >= today)
    .sort((a, b) => String(a.due_at).localeCompare(String(b.due_at)));
  if (upcoming.length) return upcoming[0];
  return [...incomplete].sort((a, b) => a.order - b.order || a.id - b.id)[0];
}

export function StudentClassPage({ data }: StudentClassPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const focusLessonId = Number(searchParams.get('lesson'));
  const [programSearch, setProgramSearch] = useState('');
  const [expandedLessons, setExpandedLessons] = useState<Set<number>>(() => {
    const next = pickNextLesson(data.lessons, Number(searchParams.get('lesson')));
    return next ? new Set([next.id]) : new Set();
  });
  const [classSelectOpen, setClassSelectOpen] = useState(false);
  const classSelectRef = useRef<HTMLDivElement>(null);
  useClickOutside(classSelectRef, () => setClassSelectOpen(false));

  useEffect(() => {
    if (!Number.isInteger(focusLessonId) || focusLessonId <= 0) return;
    setExpandedLessons((prev) => {
      if (prev.has(focusLessonId)) return prev;
      const next = new Set(prev);
      next.add(focusLessonId);
      return next;
    });
    const el = document.getElementById(`class-lesson-${focusLessonId}`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusLessonId]);

  useSeoMeta({
    title: `${data.name} — ${t('education.title')} — Sciplex`,
    description: t('classStudy.pageDesc').replace('{name}', data.name),
    canonicalPath: `/class/${data.id}`,
    noIndex: true,
  });

  const difficultyLabel = (d: string) => {
    if (d === 'Easy') return t('classEdit.diffEasy');
    if (d === 'Medium') return t('classEdit.diffMedium');
    if (d === 'Hard') return t('classEdit.diffHard');
    return d;
  };

  const statusLabel = (status: StudentProblemStatus) => {
    if (status === 'accepted') return t('classStudy.statusSolved');
    if (status === 'attempted') return t('classStudy.statusAttempted');
    return t('classStudy.statusNone');
  };

  const filteredLessons = useMemo(() => {
    const q = programSearch.trim().toLowerCase();
    if (!q) return data.lessons;
    return data.lessons
      .map((lesson) => ({
        ...lesson,
        problems: lesson.problems.filter((item) => {
          const title = item.problem?.title || `${t('classEdit.problemIdPrefix')}${item.problem_id}`;
          return title.toLowerCase().includes(q) || lesson.title.toLowerCase().includes(q);
        }),
      }))
      .filter((lesson) => lesson.title.toLowerCase().includes(q) || lesson.problems.length > 0);
  }, [data.lessons, programSearch, t]);

  const toggleLesson = (lessonId: number) => {
    setExpandedLessons((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  const { solved, total } = data.progress;
  const remaining = Math.max(0, total - solved);
  const progressPct = total > 0 ? Math.round((solved / total) * 100) : 0;
  const teacherName = teacherDisplayName(data.teacher, t('classEdit.teacherLabel'));
  const joinedClasses = data.joined_classes.length > 0
    ? data.joined_classes
    : [{ id: data.id, name: data.name }];

  const todayYmd = localTodayYmd;

  const formatDueDate = (ymd: string) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
    });
  };

  const nextLesson = useMemo(
    () => pickNextLesson(data.lessons, focusLessonId),
    [data.lessons, focusLessonId],
  );
  const nextProblem = nextLesson?.problems.find((item) => item.status !== 'accepted') || null;

  const programSearchRef = useRef(programSearch);
  useEffect(() => {
    const q = programSearch.trim();
    const wasSearching = programSearchRef.current.trim().length > 0;
    programSearchRef.current = programSearch;
    if (q) {
      const needle = q.toLowerCase();
      const ids = data.lessons
        .filter((lesson) => {
          if (lesson.title.toLowerCase().includes(needle)) return true;
          return lesson.problems.some((item) => {
            const title = item.problem?.title || '';
            return title.toLowerCase().includes(needle);
          });
        })
        .map((lesson) => lesson.id);
      setExpandedLessons(new Set(ids));
      return;
    }
    if (!wasSearching) return;
    const next = pickNextLesson(data.lessons, focusLessonId);
    setExpandedLessons(next ? new Set([next.id]) : new Set());
  }, [programSearch, data.lessons, focusLessonId]);

  return (
    <div className="class-edit-page">
      <div className="class-dash student-class">
        <aside className="class-dash__rail">
          <Link to="/my-class" className="student-class__back">
            <FaArrowLeft aria-hidden />
            {t('education.title')}
          </Link>

          <div className="class-dash__class-select-wrap" ref={classSelectRef}>
            <button
              type="button"
              className={`class-dash__class-select${classSelectOpen ? ' is-open' : ''}`}
              onClick={() => setClassSelectOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={classSelectOpen}
            >
              <span className="class-dash__class-select-icon" aria-hidden>
                <FaGraduationCap />
              </span>
              <div className="class-dash__class-select-text">
                <span className="class-dash__class-select-label">{t('classStudy.asStudent')}</span>
                <span className="class-dash__class-select-name">{data.name}</span>
              </div>
              <FaChevronDown className="class-dash__class-select-chevron" aria-hidden />
            </button>
            {classSelectOpen && (
              <div className="class-dash__class-menu" role="listbox">
                {joinedClasses.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={item.id === data.id}
                    className={`class-dash__class-menu-item${item.id === data.id ? ' is-active' : ''}`}
                    onClick={() => {
                      setClassSelectOpen(false);
                      if (item.id !== data.id) navigate(`/class/${item.id}`);
                    }}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="student-class__progress-card">
            <div
              className="student-class__ring"
              style={{ ['--ring' as string]: String(progressPct) }}
              aria-hidden
            >
              {progressPct}%
            </div>
            <div>
              <div className="student-class__progress-label">{t('classStudy.yourProgress')}</div>
              <div className="student-class__progress-value">
                {t('classStudy.solvedOf')
                  .replace('{solved}', String(solved))
                  .replace('{total}', String(total))}
              </div>
            </div>
          </div>
        </aside>

        <div className="class-dash__body">
          <header className="class-dash__hero">
            <div className="class-dash__hero-main">
              <div className="class-dash__hero-title-row">
                <h1 className="class-dash__hero-title">{data.name}</h1>
              </div>
              <p className="class-dash__hero-meta">
                {t('classEdit.teacherLabel')}: <strong>{teacherName}</strong>
              </p>
            </div>
          </header>

          <div className="class-dash__stats student-class__stats">
            <div className="class-dash__stat">
              <span className="class-dash__stat-icon class-dash__stat-icon--solved" aria-hidden>
                <FaCheck />
              </span>
              <div className="class-dash__stat-body">
                <span className="class-dash__stat-label">{t('classStudy.statSolved')}</span>
                <span className="class-dash__stat-value">{solved}</span>
              </div>
            </div>
            <div className="class-dash__stat">
              <span className="class-dash__stat-icon student-class__stat-icon--remaining" aria-hidden>
                <FaClock />
              </span>
              <div className="class-dash__stat-body">
                <span className="class-dash__stat-label">{t('classStudy.statRemaining')}</span>
                <span className="class-dash__stat-value">{remaining}</span>
              </div>
            </div>
            <div className="class-dash__stat">
              <span className="class-dash__stat-icon class-dash__stat-icon--lessons" aria-hidden>
                <FaCode />
              </span>
              <div className="class-dash__stat-body">
                <span className="class-dash__stat-label">{t('classStudy.statTotal')}</span>
                <span className="class-dash__stat-value">{total}</span>
              </div>
            </div>
          </div>

          {nextLesson && lessonIsIncomplete(nextLesson) && (
            <section className="class-dash__panel student-class__next">
              <div className="student-class__next-copy">
                <h2 className="class-dash__panel-title">{t('classStudy.currentWork')}</h2>
                <p className="student-class__next-title">{nextLesson.title}</p>
                {nextLesson.due_at ? (
                  <p className={`student-class__next-due${nextLesson.due_at < todayYmd() ? ' is-overdue' : ''}`}>
                    {nextLesson.due_at < todayYmd()
                      ? t('classStudy.overdue')
                      : t('classStudy.dueBy').replace('{date}', formatDueDate(nextLesson.due_at))}
                  </p>
                ) : null}
              </div>
              {nextProblem ? (
                <Link
                  to={studyProblemTo(nextProblem, data.id, nextLesson.id)}
                  className="class-edit-btn class-edit-btn-primary student-class__next-cta"
                >
                  {t('education.continueWork')}
                </Link>
              ) : (
                <button
                  type="button"
                  className="class-edit-btn class-edit-btn-primary student-class__next-cta"
                  onClick={() => {
                    setExpandedLessons(new Set([nextLesson.id]));
                    document.getElementById(`class-lesson-${nextLesson.id}`)
                      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  }}
                >
                  {t('education.continueWork')}
                </button>
              )}
            </section>
          )}

          <section className="class-dash__panel">
            <div className="class-dash__panel-head">
              <h2 className="class-dash__panel-title">{t('classEdit.courseProgram')}</h2>
              <label className="class-dash__search">
                <FaSearch aria-hidden />
                <input
                  type="search"
                  value={programSearch}
                  onChange={(e) => setProgramSearch(e.target.value)}
                  placeholder={t('classEdit.searchProgram')}
                />
              </label>
            </div>

            <ul className="class-dash__lessons">
              {filteredLessons.length === 0 ? (
                <li className="class-dash__empty">{t('classStudy.programEmpty')}</li>
              ) : (
                filteredLessons.map((lesson, idx) => {
                  const isOpen = expandedLessons.has(lesson.id);
                  const lessonSolved = lesson.problems.filter((p) => p.status === 'accepted').length;
                  return (
                    <li
                      key={lesson.id}
                      id={`class-lesson-${lesson.id}`}
                      className={`class-dash__lesson${isOpen ? ' is-open' : ''}${
                        focusLessonId === lesson.id || nextLesson?.id === lesson.id ? ' is-focus' : ''
                      }`}
                    >
                      <div className="class-dash__lesson-head">
                        <button
                          type="button"
                          className="class-dash__lesson-toggle"
                          onClick={() => toggleLesson(lesson.id)}
                          aria-expanded={isOpen}
                        >
                          <FaChevronRight className="class-dash__lesson-chevron" aria-hidden />
                          <span className="class-dash__lesson-index">{idx + 1}</span>
                          <span className="class-dash__lesson-title">{lesson.title}</span>
                          {lesson.due_at ? (
                            <span className={`class-dash__lesson-badge${lesson.due_at < todayYmd() && lesson.problems.some((p) => p.status !== 'accepted') ? ' is-overdue' : ''}`}>
                              <FaClock aria-hidden />
                              {lesson.due_at < todayYmd() && lesson.problems.some((p) => p.status !== 'accepted')
                                ? t('classStudy.overdue')
                                : t('classStudy.dueBy').replace('{date}', formatDueDate(lesson.due_at))}
                            </span>
                          ) : null}
                          <span className="class-dash__lesson-count">
                            {lessonSolved}/{lesson.problems.length} {t('classEdit.tasksShort')}
                          </span>
                        </button>
                      </div>
                      {isOpen && (
                        <div className="class-dash__lesson-body">
                          {lesson.problems.length === 0 ? (
                            <p className="class-dash__empty">{t('classStudy.lessonEmpty')}</p>
                          ) : (
                            lesson.problems.map((item) => (
                              <div key={item.id} className="class-dash__task-row">
                                <Link
                                  to={studyProblemTo(item, data.id, lesson.id)}
                                  className={`class-dash__task student-class__task student-class__task--${item.status}`}
                                >
                                  <span
                                    className={`student-class__status student-class__status--${item.status}`}
                                    title={statusLabel(item.status)}
                                  >
                                    {item.status === 'accepted' ? (
                                      <FaCheck aria-hidden />
                                    ) : item.status === 'attempted' ? (
                                      <FaClock aria-hidden />
                                    ) : (
                                      <FaCode aria-hidden />
                                    )}
                                  </span>
                                  <span className="class-dash__task-title">
                                    {item.problem?.title ?? `${t('classEdit.problemIdPrefix')}${item.problem_id}`}
                                    {item.has_feedback ? (
                                      <span className={`student-class__feedback-badge${item.feedback_unread ? ' is-unread' : ''}`}>
                                        <FaCommentDots aria-hidden />
                                        {item.feedback_unread
                                          ? t('classStudy.hasFeedbackUnread')
                                          : t('classStudy.hasFeedback')}
                                      </span>
                                    ) : null}
                                  </span>
                                  {item.problem?.difficulty ? (
                                    <span
                                      className="class-dash__task-diff"
                                      style={{ color: difficultyColor(item.problem.difficulty) }}
                                    >
                                      {difficultyLabel(item.problem.difficulty)}
                                    </span>
                                  ) : null}
                                </Link>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
